import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { observer } from 'mobx-react-lite';
import { useRootStore } from '../../../../../../providers/root-store-provider';
import { routes } from '../../../../../../config/routes';
import { CardDashboardTradesLoading } from '../card-loading/card-dashboard-trades-loading';
import { IconArrowRightWide } from '../../../../icons/arrow-right-wide';
import { IconButton } from '../../../../icon/button/icon-button';
import { ValueAmount } from '../../../../value/amount/value-amount';
import { CardTitle } from '../../../title/card-title';
import { ValueDescription } from '../../../value-description/value-description';

type CardDashboardTradesInvestedValueType = {
    shared?: boolean;
};

export const CardDashboardTradesInvestedValue: React.FC<CardDashboardTradesInvestedValueType> = observer(
    ({ shared = false }: CardDashboardTradesInvestedValueType) => {
        const { t } = useTranslation();
        const router = useRouter();

        const rootStore = useRootStore();
        const { portfolioStore, statisticStore } = rootStore;
        const { overviewStore } = statisticStore;
        const { overview } = overviewStore;

        const portfolioCurrency = portfolioStore.selectedPortfolio.portfolioHistoryCurrencyCode;

        return (
            <CardDashboardTradesLoading fields={['invested', 'investmentPeriod', 'averageInvestment']}>
                <div className="relative flex h-full flex-col items-stretch justify-between overflow-hidden rounded-large bg-surface p-[1.6rem] shadow-strong lg:p-[2.2rem]">
                    <CardTitle
                        title={t('layout.statisticsCard.investedValue.title')}
                        tooltip={t('layout.statisticsCard.investedValue.tooltip')}
                        actions={
                            shared || (
                                <IconButton
                                    icon={<IconArrowRightWide />}
                                    variation={'outlined'}
                                    size={'small'}
                                    onClick={async () => {
                                        await router.push(`${routes.app.invested}`);
                                    }}
                                />
                            )
                        }
                    />
                    <div className="gap-xsmall">
                        {overview && (
                            <>
                                <ValueDescription
                                    padding={'none'}
                                    value={
                                        <ValueAmount
                                            amount={overview.invested}
                                            currency={portfolioCurrency}
                                            mode="pure"
                                            hidable
                                        />
                                    }
                                    description={
                                        <Trans
                                            i18nKey={`layout.statisticsCard.investedValue.assets`}
                                            values={{
                                                numberOfAssets: portfolioStore.selectedPortfolio.numberOfAssets,
                                                numberOfTrades: portfolioStore.selectedPortfolio.numberOfTrades,
                                            }}
                                            components={{ strong: <strong /> }}
                                        />
                                    }
                                    valueColor={'secondary'}
                                />

                                <br />

                                <div className="text-medium leading-vlarge">
                                    {t('layout.statisticsCard.investedValue.investmentPeriod')}
                                </div>
                                <div className="text-medium leading-vlarge font-bold">
                                    {t('layout.statisticsCard.investedValue.months', {
                                        value: overview.investmentPeriod,
                                    })}
                                </div>

                                <br />

                                <div className="text-medium leading-vlarge">
                                    {t('layout.statisticsCard.investedValue.monthlyAverage')}
                                </div>
                                <div className="text-medium leading-vlarge font-bold">
                                    <ValueAmount
                                        amount={overview.averageInvestment}
                                        currency={portfolioCurrency}
                                        mode="pure"
                                        hidable
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </CardDashboardTradesLoading>
        );
    },
);
